import { useMemo, useState } from "react";
import { TaskDisclosure } from "../components/TaskDisclosure";

function statusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function dateOnly(value) {
  if (!value) {
    return "Not scheduled";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function dateInput(value) {
  if (!value) {
    return "";
  }
  return String(value).slice(0, 10);
}

function isMaintenanceRequired(resource) {
  const dueAt = resource.attributes?.maintenanceDueAt ? Date.parse(resource.attributes.maintenanceDueAt) : Number.NaN;
  return resource.status === "maintenance" ||
    resource.attributes?.rentalStatus === "maintenance" ||
    (Number.isFinite(dueAt) && dueAt <= Date.now() + 14 * 24 * 60 * 60 * 1000);
}

function queueReason(resource) {
  if (resource.status === "maintenance" || resource.attributes?.rentalStatus === "maintenance") {
    return "Out of service";
  }
  if (resource.attributes?.maintenanceDueAt && Date.parse(resource.attributes.maintenanceDueAt) < Date.now()) {
    return "Past due";
  }
  if (resource.attributes?.maintenanceDueAt) {
    return "Due soon";
  }
  return "Review";
}

function hasMaintenanceTracking(resource) {
  return Boolean(
    resource.status === "maintenance" ||
    resource.attributes?.maintenancePlanName ||
    resource.attributes?.maintenanceDueAt ||
    resource.attributes?.maintenanceNotes ||
    resource.attributes?.assetClass ||
    resource.attributes?.serialNumber ||
    (resource.attributes?.rentalStatus && resource.attributes.rentalStatus !== "not_rental")
  );
}

function maintenanceNotes(resource) {
  const noteText = resource.attributes?.maintenanceNotes || "";
  return noteText
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 3);
}

export function EquipmentPage({
  loading,
  onExportResources,
  onUpdateResource,
  resources = [],
  updatingResourceId
}) {
  const [activityDrafts, setActivityDrafts] = useState({});
  const trackedAssets = useMemo(() => resources.filter(hasMaintenanceTracking), [resources]);
  const rentalAssets = useMemo(() => trackedAssets.filter((resource) => (
    resource.attributes?.rentalStatus && resource.attributes.rentalStatus !== "not_rental"
  )), [trackedAssets]);
  const maintenanceQueue = useMemo(() => trackedAssets.filter(isMaintenanceRequired), [trackedAssets]);
  const overdueAssets = useMemo(() => maintenanceQueue.filter((resource) => (
    resource.attributes?.maintenanceDueAt && Date.parse(resource.attributes.maintenanceDueAt) < Date.now()
  )), [maintenanceQueue]);
  const dueSoonAssets = useMemo(() => maintenanceQueue.filter((resource) => (
    queueReason(resource) === "Due soon"
  )), [maintenanceQueue]);
  const outOfServiceAssets = useMemo(() => maintenanceQueue.filter((resource) => (
    resource.status === "maintenance" || resource.attributes?.rentalStatus === "maintenance"
  )), [maintenanceQueue]);

  function draftFor(resourceId) {
    return activityDrafts[resourceId] || {
      action: "inspection",
      note: "",
      nextDueAt: ""
    };
  }

  function updateDraft(resourceId, patch) {
    setActivityDrafts((current) => ({
      ...current,
      [resourceId]: {
        ...draftFor(resourceId),
        ...patch
      }
    }));
  }

  function logMaintenanceActivity(event, resource) {
    event.preventDefault();
    const draft = draftFor(resource.resourceId);
    if (!draft.note.trim()) {
      return;
    }
    const timestamp = new Date().toISOString();
    const nextLine = `${dateOnly(timestamp)} / ${statusLabel(draft.action)} / ${draft.note.trim()}`;
    const nextEvent = {
      action: draft.action,
      title: statusLabel(draft.action),
      note: draft.note.trim(),
      source: "maintenance_hub",
      createdAt: timestamp
    };
    onUpdateResource(resource.resourceId, {
      attributes: {
        ...(resource.attributes || {}),
        maintenanceDueAt: draft.nextDueAt || resource.attributes?.maintenanceDueAt || "",
        maintenanceLastCompletedAt: timestamp,
        maintenanceLastActionAt: timestamp,
        maintenanceLastActionSource: "maintenance_hub",
        maintenanceNotes: [nextLine, ...(maintenanceNotes(resource) || [])].join("\n"),
        maintenanceEvents: [nextEvent, ...(resource.attributes?.maintenanceEvents || [])].slice(0, 20)
      }
    });
    setActivityDrafts((current) => ({
      ...current,
      [resource.resourceId]: {
        action: "inspection",
        note: "",
        nextDueAt: ""
      }
    }));
  }

  function returnToService(resource) {
    onUpdateResource(resource.resourceId, {
      status: "open",
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus: resource.attributes?.rentalStatus && resource.attributes.rentalStatus !== "not_rental"
          ? "available"
          : resource.attributes?.rentalStatus || "not_rental",
        checkedOutMemberId: null,
        checkedOutMemberName: null,
        checkedOutAt: null
      }
    });
  }

  function takeOutOfService(resource) {
    onUpdateResource(resource.resourceId, {
      status: "maintenance",
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus: resource.attributes?.rentalStatus && resource.attributes.rentalStatus !== "not_rental"
          ? "maintenance"
          : resource.attributes?.rentalStatus || "not_rental"
      }
    });
  }

  return (
    <div className="ops-page maintenance-hub-page">
      {loading && <div className="status-strip">Loading maintenance records...</div>}
      <section className="membership-section-overview operations-section-overview maintenance-command-overview">
        <div>
          <span className="section-kicker">Maintenance</span>
          <h3>Maintenance Hub</h3>
          <p>Work only the facilities and assets that need attention. Permanent facility setup stays in Range Facilities; same-day line control stays in Range Operations.</p>
        </div>
        <div className="membership-context-metrics operations-context-metrics">
          <div>
            <span>Needs work</span>
            <strong>{maintenanceQueue.length}</strong>
            <small>out of service, overdue, or due soon</small>
          </div>
          <div>
            <span>Out of service</span>
            <strong>{outOfServiceAssets.length}</strong>
            <small>cannot be used now</small>
          </div>
          <div>
            <span>Past due</span>
            <strong>{overdueAssets.length}</strong>
            <small>missed due date</small>
          </div>
          <div>
            <span>Due soon</span>
            <strong>{dueSoonAssets.length}</strong>
            <small>next 14 days</small>
          </div>
        </div>
      </section>

      <section className="panel ops-support-panel">
        <div className="panel-header">
          <div>
            <h2>Maintenance queue</h2>
            <p>{trackedAssets.length} tracked item{trackedAssets.length === 1 ? "" : "s"} monitored, including {rentalAssets.length} rental asset{rentalAssets.length === 1 ? "" : "s"}.</p>
          </div>
          <button type="button" className="secondary-button" onClick={onExportResources}>Export CSV</button>
        </div>

        <div className="maintenance-queue-list">
          {maintenanceQueue.map((resource) => {
            const draft = draftFor(resource.resourceId);
            const isUpdating = updatingResourceId === resource.resourceId;
            const isOutOfService = resource.status === "maintenance" || resource.attributes?.rentalStatus === "maintenance";
            return (
              <article className="maintenance-queue-card" key={resource.resourceId}>
                <div className="maintenance-card-main">
                  <div className="maintenance-card-title">
                    <div>
                      <strong>{resource.name}</strong>
                      <span>{resource.attributes?.maintenancePlanName || resource.attributes?.assetClass || statusLabel(resource.type)}</span>
                    </div>
                    <span className={isOutOfService ? "status-badge status-warning" : "status-badge status-muted"}>
                      {queueReason(resource)}
                    </span>
                  </div>

                  <dl className="maintenance-facts">
                    <div>
                      <dt>Due date</dt>
                      <dd>{dateOnly(resource.attributes?.maintenanceDueAt)}</dd>
                    </div>
                    <div>
                      <dt>Plan</dt>
                      <dd>{resource.attributes?.maintenanceIntervalDays ? `Every ${resource.attributes.maintenanceIntervalDays} days` : "No interval"}</dd>
                    </div>
                    <div>
                      <dt>Owner</dt>
                      <dd>{resource.attributes?.maintenanceAssignedTo || "Unassigned"}</dd>
                    </div>
                  </dl>

                  {resource.attributes?.maintenanceChecklist && (
                    <div className="maintenance-log-preview">
                      <strong>Checklist</strong>
                      {resource.attributes.maintenanceChecklist
                        .split("\n")
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .slice(0, 4)
                        .map((item) => <span key={item}>{item}</span>)}
                    </div>
                  )}

                  <div className="maintenance-log-preview">
                    <strong>Recent activity</strong>
                    {maintenanceNotes(resource).length > 0 ? (
                      maintenanceNotes(resource).map((note) => <span key={note}>{note}</span>)
                    ) : (
                      <span>No maintenance activity logged yet.</span>
                    )}
                    {(resource.statusHistory || []).slice(0, 2).map((item, index) => (
                      <span key={item.historyId || `${item.changedAt}-${index}`}>
                        Status: {statusLabel(item.from?.status)} to {statusLabel(item.to?.status)} / {dateOnly(item.changedAt)}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="maintenance-card-actions">
                  <TaskDisclosure
                    actionLabel="Log activity"
                    closeLabel="Close"
                    title="Log maintenance activity"
                    description="Add work notes without changing the asset state."
                  >
                    <form className="stacked-form" onSubmit={(event) => logMaintenanceActivity(event, resource)}>
                      <div className="form-row">
                        <label>
                          Activity
                          <select value={draft.action} onChange={(event) => updateDraft(resource.resourceId, { action: event.target.value })}>
                            <option value="inspection">Inspection</option>
                            <option value="cleaning">Cleaning</option>
                            <option value="repair">Repair</option>
                            <option value="parts_ordered">Parts ordered</option>
                            <option value="function_check">Function check</option>
                          </select>
                        </label>
                        <label>
                          Next due
                          <input type="date" value={draft.nextDueAt || dateInput(resource.attributes?.maintenanceDueAt)} onChange={(event) => updateDraft(resource.resourceId, { nextDueAt: event.target.value })} />
                        </label>
                      </div>
                      <label>
                        Work note
                        <textarea value={draft.note} onChange={(event) => updateDraft(resource.resourceId, { note: event.target.value })} rows={4} placeholder="What was inspected, repaired, cleaned, or still needs follow-up?" />
                      </label>
                      <button type="submit" disabled={isUpdating || !draft.note.trim()}>Save activity</button>
                    </form>
                  </TaskDisclosure>

                  <div className="row-actions compact-actions">
                    {!isOutOfService && (
                      <button type="button" className="secondary-button" disabled={isUpdating} onClick={() => takeOutOfService(resource)}>
                        Take offline
                      </button>
                    )}
                    <button type="button" disabled={isUpdating} onClick={() => returnToService(resource)}>
                      Return to service
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {maintenanceQueue.length === 0 && (
          <div className="empty-state">No facilities or assets currently require maintenance. Items appear here when they are out of service, overdue, or due soon.</div>
        )}
      </section>
    </div>
  );
}
